/**
 * Distribution de la durée des sessions.
 *
 * Histogramme borné au 99e percentile : sans cette borne, quelques sessions de
 * plusieurs heures étireraient l'axe et tasseraient tout le reste dans la
 * première barre. Les sessions plus longues ne sont pas jetées, elles sont
 * comptées dans la dernière barre — elles existent, elles ne doivent simplement
 * pas fixer l'échelle.
 *
 * Moyenne ET médiane sont tracées : sur une distribution aussi dissymétrique,
 * l'écart entre les deux est l'information. Une moyenne très au-dessus de la
 * médiane dit qu'une minorité de sessions très longues tire le chiffre moyen.
 */

import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatDuration, formatInteger } from '../lib/format';
import { AXIS_STYLE, COLORS } from '../lib/theme';
import type { DataColumn, SessionDurationBucket } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<SessionDurationBucket>> = [
  {
    key: 'range',
    header: 'Durée',
    render: (row) => `${formatDuration(row.fromSeconds)} – ${formatDuration(row.toSeconds)}`,
  },
  { key: 'sessions', header: 'Sessions', numeric: true, render: (row) => formatInteger(row.sessions) },
];

interface Props {
  data: readonly SessionDurationBucket[];
  meanSeconds: number;
  medianSeconds: number;
}

export function SessionDurationHistogram({ data, meanSeconds, medianSeconds }: Props) {
  return (
    <ChartCard
      title="Durée des sessions"
      subtitle="Borné au 99e percentile — les sessions plus longues sont dans la dernière barre"
      rows={data}
      columns={columns}
      chartHeight={300}
    >
      <ResponsiveContainer width="100%" height="100%">
        {/* Marge haute genereuse : les deux etiquettes de repere sont posees
            au-dessus de l'aire de trace. */}
        <BarChart data={data as SessionDurationBucket[]} margin={{ top: 26, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="fromSeconds"
            type="number"
            // Echelle numerique et non categorielle : les seaux sont d'egale
            // largeur, et les lignes de repere doivent pouvoir se poser a la
            // bonne abscisse.
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value: number) => formatDuration(value)}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={36}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={
              <ChartTooltip
                labelFormatter={(label) => `à partir de ${formatDuration(Number(label))}`}
                valueFormatter={formatInteger}
              />
            }
          />
          <Bar isAnimationActive={false} dataKey="sessions" name="Sessions" fill={COLORS.series1} radius={[2, 2, 0, 0]} />

          {/* Les reperes sont des ANNOTATIONS, pas des series : ils portent une
              couleur de texte, pas une couleur de serie. */}
          <ReferenceLine
            x={medianSeconds}
            stroke={COLORS.brandCyan}
            strokeWidth={1}
            label={{ value: `médiane ${formatDuration(medianSeconds)}`, position: 'top', fill: COLORS.textSecondary, fontSize: 11 }}
          />
          <ReferenceLine
            x={meanSeconds}
            stroke={COLORS.textMuted}
            strokeWidth={1}
            strokeDasharray="4 3"
            label={{ value: `moyenne ${formatDuration(meanSeconds)}`, position: 'insideTopRight', fill: COLORS.textMuted, fontSize: 11 }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
